"use client";

import React, { useMemo, useState, useEffect } from "react";
import { ErrorBoundary } from "react-error-boundary";
import { useLocation } from "react-router-dom";
import {
  SidebarGroup,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  useSidebar,
} from "@/components/ui/sidebar";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
} from "@/components/ui/hover-card";
import { Skeleton } from "@/components/ui/skeleton";
import { ThreadRenameDialog } from "@/features/sidebar/projects/dialogs";
import { cn } from "@/lib/utils";
import { useAuthStore } from "@/store";
import { useThreadNewTracking } from "@/features/sidebar/projects/hooks/useThreadNewTracking";
import {
  ChevronDown,
  ChevronRight,
  MessageSquare,
  MoreHorizontal,
} from "lucide-react";
import { TbPinFilled } from "react-icons/tb";
import { useHistoryThreads } from "../history/hooks/useHistoryThreads";
import { useHistoryActions } from "../history/hooks/useHistoryActions";
import { SortableHistoryItem } from "../history/components/SortableHistoryItem";
import {
  SortableContext,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { useDroppable } from "@dnd-kit/core";
import { useSidebarDndContext } from "@/contexts/SidebarDndProvider";

const ChatHistoryFallback = () => {
  return (
    <div className="space-y-2 py-4">
      <Skeleton className="h-4 w-3/4" />
      <Skeleton className="h-4 w-1/2" />
      <Skeleton className="h-4 w-2/3" />
    </div>
  );
};

/**
 * NavHistory - Chats component using sidebar-07 pattern
 *
 * Provides chat list sorted by most recent first, drag-and-drop support,
 * and thread management actions.
 */
const NavHistoryContent = ({ searchQuery = "" }) => {
  const location = useLocation();
  const { workspaces, setWorkspaces } = useAuthStore();
  const { markThreadAsNew, isThreadNew } = useThreadNewTracking();
  const { threads } = useHistoryThreads();
  const [showAllChats, setShowAllChats] = useState(false);

  const {
    isLoading,
    openDropdownId,
    threadRenameDialogOpen,
    threadToRename,
    newThreadName,
    isRenamingThread,
    setOpenDropdownId,
    setThreadRenameDialogOpen,
    setNewThreadName,
    handleThreadSelect,
    handleThreadDelete,
    handleThreadRenameSubmit,
    handleThreadRenameCancel,
    startRenaming,
    handleToggleBookmark,
    handleThreadClone,
    handleMoveToProject,
  } = useHistoryActions({
    workspaces,
    setWorkspaces,
    markThreadAsNew,
  });

  // Filter and sort threads by most recent first
  const sortedThreads = useMemo(() => {
    if (!threads || threads.length === 0) return [];

    // Filter by search query if provided
    let filtered = threads;
    if (searchQuery && searchQuery.trim() !== "") {
      const query = searchQuery.toLowerCase().trim();
      filtered = threads.filter((thread) => {
        const title = (thread?.title || "").toLowerCase();
        return title.includes(query);
      });
    }

    // Sort: pinned first, then by most recent
    return [...filtered].sort((a, b) => {
      // Pinned items come first
      const aPinned = a?.metadata?.bookmarked ? 1 : 0;
      const bPinned = b?.metadata?.bookmarked ? 1 : 0;
      if (bPinned !== aPinned) {
        return bPinned - aPinned;
      }
      // Then sort by most recent first
      const dateA = new Date(a?.updated_at || a?.lastMessageAt || 0);
      const dateB = new Date(b?.updated_at || b?.lastMessageAt || 0);
      return dateB - dateA;
    });
  }, [threads, searchQuery]);

  // Note: Drag-and-drop handling is centralized in SidebarDndProvider
  // via useSidebarDndHandlers hook

  // Empty state
  if (!sortedThreads?.length) {
    return (
      <div className="px-4 py-8 text-center">
        <div className="flex justify-center mb-3">
          <MessageSquare className="h-10 w-10 text-purple-500 opacity-80" />
        </div>
        <h3 className="text-sm font-medium text-sidebar-foreground mb-1">
          No chats yet
        </h3>
        <p className="text-xs text-sidebar-foreground/60">
          Start a conversation to see your chats here
        </p>
      </div>
    );
  }

  // Show first 10 when collapsed, all when expanded
  const displayThreads = showAllChats
    ? sortedThreads
    : sortedThreads.slice(0, 10);

  const totalThreadCount = sortedThreads.length;
  const hasMoreThan10 = totalThreadCount > 10;

  return (
    <div className="w-full relative">
      <SidebarMenu className="w-full">
        <SortableContext
          items={displayThreads.map((t) => t.id)}
          strategy={verticalListSortingStrategy}
        >
          {displayThreads.map((thread) => (
            <SortableHistoryItem
              key={`${thread?.id}-${thread.updated_at || Date.now()}`}
              thread={thread}
              openDropdownId={openDropdownId}
              setOpenDropdownId={setOpenDropdownId}
              isThreadNew={isThreadNew}
              handleThreadSelect={handleThreadSelect}
              startRenaming={startRenaming}
              handleThreadDelete={handleThreadDelete}
              handleToggleBookmark={handleToggleBookmark}
              handleThreadClone={handleThreadClone}
              handleMoveToProject={handleMoveToProject}
            />
          ))}
        </SortableContext>
      </SidebarMenu>

      {hasMoreThan10 && (
        <SidebarMenu className="w-full min-w-0 max-w-full">
          <SidebarMenuItem className="w-full min-w-0 max-w-full">
            <SidebarMenuButton
              onClick={() => setShowAllChats(!showAllChats)}
              className="w-full min-w-0 max-w-full text-sidebar-foreground/70"
            >
              <MoreHorizontal className="h-4 w-4 flex-shrink-0 text-sidebar-foreground/70" />
              <span className="truncate min-w-0">
                {showAllChats ? "Show less" : "Show more"}
              </span>
            </SidebarMenuButton>
          </SidebarMenuItem>
        </SidebarMenu>
      )}

      <ThreadRenameDialog
        isOpen={threadRenameDialogOpen}
        onOpenChange={setThreadRenameDialogOpen}
        threadToRename={threadToRename}
        newThreadName={newThreadName}
        setNewThreadName={setNewThreadName}
        isRenaming={isRenamingThread}
        onRename={handleThreadRenameSubmit}
        onCancel={handleThreadRenameCancel}
      />
    </div>
  );
};

function NavHistory({ searchQuery = "" }) {
  const [isExpanded, setIsExpanded] = useState(true);
  const [collapsedChatsLimit, setCollapsedChatsLimit] = useState(50);
  const [isHoveringChats, setIsHoveringChats] = useState(false);
  const [isHoveringChatsHeader, setIsHoveringChatsHeader] = useState(false);
  const [isHoverCardOpen, setIsHoverCardOpen] = useState(false);
  const { isMobile, state: sidebarState } = useSidebar();
  // Show collapsed HoverCard view only on desktop when sidebar is collapsed
  // On mobile, always show inline content in the Sheet drawer
  const isCollapsed = !isMobile && sidebarState === "collapsed";
  const { threads } = useHistoryThreads();
  const collapsedChatsScrollRef = React.useRef(null);

  // Drag-and-drop: Make Chats section a drop target
  const { draggedItemType, activeId, draggedItem } = useSidebarDndContext();

  const { setNodeRef: setChatsDroppableRef, isOver: isOverChats } =
    useDroppable({
      id: "chats-drop-target",
      data: {
        type: "chats",
        workspaceId: null,
      },
    });

  // Show drop indicator only when dragging a chat FROM a project
  const isDraggingChatFromProject =
    draggedItemType === "chat" &&
    activeId &&
    (draggedItem?.workspace_id || draggedItem?.workspaceId);

  // Close HoverCard when sidebar expands from collapsed state
  useEffect(() => {
    if (!isCollapsed && isHoverCardOpen) {
      setIsHoverCardOpen(false);
    }
  }, [isCollapsed, isHoverCardOpen]);

  // Handle scroll for loading more chats in collapsed view
  const handleCollapsedChatsScroll = React.useCallback(
    (e) => {
      const element = e.currentTarget;
      const isNearBottom =
        element.scrollHeight - element.scrollTop <= element.clientHeight + 50;

      if (isNearBottom && collapsedChatsLimit < (threads?.length || 0)) {
        setCollapsedChatsLimit((prev) =>
          Math.min(prev + 50, threads?.length || 0)
        );
      }
    },
    [collapsedChatsLimit, threads?.length]
  );

  // Render collapsed icon with hover card
  if (isCollapsed) {
    // Get recent threads for hover preview (pinned first, then by most recent)
    const sortedThreads = [...(threads || [])].sort((a, b) => {
      // Pinned items come first
      const aPinned = a?.metadata?.bookmarked ? 1 : 0;
      const bPinned = b?.metadata?.bookmarked ? 1 : 0;
      if (bPinned !== aPinned) {
        return bPinned - aPinned;
      }
      // Then sort by most recent first
      const dateA = new Date(a?.updated_at || a?.lastMessageAt || 0);
      const dateB = new Date(b?.updated_at || b?.lastMessageAt || 0);
      return dateB - dateA;
    });
    const recentThreads = sortedThreads.slice(0, collapsedChatsLimit);

    return (
      <ErrorBoundary FallbackComponent={ChatHistoryFallback}>
        <SidebarGroup className="w-full min-w-0 max-w-full">
          <SidebarMenu>
            <SidebarMenuItem className="w-full min-w-0 max-w-full">
              <HoverCard
                openDelay={200}
                open={isHoverCardOpen}
                onOpenChange={setIsHoverCardOpen}
              >
                <HoverCardTrigger asChild>
                  <SidebarMenuButton
                    size="icon"
                    className="w-full justify-center"
                    onMouseEnter={() => setIsHoveringChats(true)}
                    onMouseLeave={() => setIsHoveringChats(false)}
                  >
                    {isHoveringChats ? (
                      <ChevronRight className="h-4 w-4" />
                    ) : (
                      <MessageSquare className="h-4 w-4" />
                    )}
                  </SidebarMenuButton>
                </HoverCardTrigger>
                <HoverCardContent
                  side="right"
                  align="start"
                  sideOffset={12}
                  className="w-80 p-3"
                >
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <h4 className="text-xs font-semibold flex items-center gap-2 text-gray-900 dark:text-white">
                        <MessageSquare className="h-5 w-5" />
                        Chats
                      </h4>
                      <span className="text-xs text-gray-500 dark:text-white/50">
                        {threads?.length || 0} chats
                      </span>
                    </div>
                    <div
                      ref={collapsedChatsScrollRef}
                      onScroll={handleCollapsedChatsScroll}
                      className="space-y-1 max-h-96 overflow-y-auto"
                    >
                      {recentThreads.length > 0 ? (
                        <>
                          {recentThreads.map((thread) => (
                            <a
                              key={thread.id}
                              href={`/chat/${thread.id}`}
                              className={cn(
                                "w-full flex items-center gap-3 px-3 py-1.5 rounded-xl text-xs font-medium",
                                "transform-gpu hover:scale-[1.02] hover:shadow-md active:scale-[0.98]",
                                "transition-[background-color,transform] duration-200",
                                "text-gray-900 dark:text-white hover:bg-zinc-100 dark:hover:bg-zinc-700/70"
                              )}
                            >
                              {thread.metadata?.bookmarked ? (
                                <TbPinFilled className="h-4 w-4 flex-shrink-0 text-sidebar-foreground/60" />
                              ) : (
                                <MessageSquare className="h-5 w-5 flex-shrink-0" />
                              )}
                              <span className="truncate min-w-0">
                                {thread.title}
                              </span>
                            </a>
                          ))}
                          {collapsedChatsLimit < (threads?.length || 0) && (
                            <div className="text-xs text-muted-foreground text-center py-2">
                              Loading more...
                            </div>
                          )}
                        </>
                      ) : (
                        <div className="text-xs text-muted-foreground text-center py-4">
                          No chats yet
                        </div>
                      )}
                    </div>
                  </div>
                </HoverCardContent>
              </HoverCard>
            </SidebarMenuItem>
          </SidebarMenu>
        </SidebarGroup>
      </ErrorBoundary>
    );
  }

  return (
    <ErrorBoundary FallbackComponent={ChatHistoryFallback}>
      <Collapsible
        open={isExpanded}
        onOpenChange={setIsExpanded}
        className="group/collapsible w-full min-w-0 max-w-full"
        style={{ maxWidth: "calc(var(--sidebar-width) - 10px)" }}
      >
        <SidebarGroup className="w-full min-w-0 max-w-full">
          <SidebarMenu className="w-full min-w-0 max-w-full">
            <SidebarMenuItem className="w-full min-w-0 max-w-full">
              {/* Wrap header with droppable container when collapsed and dragging */}
              <div
                ref={
                  !isExpanded && isDraggingChatFromProject
                    ? setChatsDroppableRef
                    : undefined
                }
                className={cn(
                  "flex items-center w-full",
                  !isExpanded &&
                    isDraggingChatFromProject &&
                    isOverChats &&
                    "ring-2 ring-purple-500/50 ring-offset-2 ring-offset-sidebar rounded-md"
                )}
              >
                <CollapsibleTrigger
                  asChild
                  className="w-full min-w-0 max-w-full flex-1"
                >
                  <SidebarMenuButton
                    tooltip="Chats"
                    className="w-full min-w-0 max-w-full data-[state=open]:bg-sidebar data-[state=open]:text-sidebar-foreground data-[state=open]:hover:bg-sidebar-accent data-[state=open]:hover:text-sidebar-accent-foreground"
                    onMouseEnter={() => setIsHoveringChatsHeader(true)}
                    onMouseLeave={() => setIsHoveringChatsHeader(false)}
                  >
                    {isHoveringChatsHeader ? (
                      isExpanded ? (
                        <ChevronDown className="h-4 w-4 flex-shrink-0" />
                      ) : (
                        <ChevronRight className="h-4 w-4 flex-shrink-0" />
                      )
                    ) : (
                      <MessageSquare className="h-4 w-4 flex-shrink-0" />
                    )}
                    <span className="truncate min-w-0 group-data-[collapsible=icon]:hidden">
                      Chats
                    </span>
                  </SidebarMenuButton>
                </CollapsibleTrigger>
                {/* Show "drop here" text when collapsed and dragging */}
                {!isExpanded && isDraggingChatFromProject && (
                  <span
                    className={cn(
                      "ml-2 text-xs whitespace-nowrap transition-colors",
                      isOverChats ? "text-purple-500" : "text-muted-foreground"
                    )}
                  >
                    drop here
                  </span>
                )}
              </div>
            </SidebarMenuItem>
          </SidebarMenu>
          <CollapsibleContent>
            {/* Drop zone when expanded and dragging a chat from a project */}
            {isDraggingChatFromProject && (
              <div
                ref={isExpanded ? setChatsDroppableRef : undefined}
                className={cn(
                  "mx-2 my-1 p-3 border-2 border-dashed rounded-md text-center text-sm transition-colors",
                  isOverChats
                    ? "border-purple-500 bg-purple-500/10 text-purple-600 dark:text-purple-400"
                    : "border-muted-foreground/30 text-muted-foreground"
                )}
              >
                drop here
              </div>
            )}
            <NavHistoryContent searchQuery={searchQuery} />
          </CollapsibleContent>
        </SidebarGroup>
      </Collapsible>
    </ErrorBoundary>
  );
}

// Memoize NavHistory - only rerender if searchQuery changes
// This prevents ScrollArea from resetting when navigating between pages
const NavHistoryMemoized = React.memo(NavHistory, (prevProps, nextProps) => {
  // Only rerender if searchQuery changes, not when location changes
  return prevProps.searchQuery === nextProps.searchQuery;
});

export { NavHistoryMemoized as NavHistory };
